/**
 * @file THE CAR RADIO — the model. What station the dial is on, which track it
 * is playing, and HOW FAR THROUGH IT the station has got. It makes no sound: no
 * `AudioContext`, no element, no fetch and no url of its own beyond joining the
 * one the content already wrote down.
 *
 * The split is `systems/Speech.js` against `render/SpeechBubbles.js`, and
 * `audio/mixer.js` against `audio/AudioBackend.js`, taken a third time:
 * everything here is provable in node with duck types, and `systems/GameAudio.js`
 * is the only thing that turns `'radio:track'` into an actual noise.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠⚠ THE ONE IDEA: THE STATION IS ALWAYS ON AIR, AND A CAR IS A WINDOW ONTO IT
 * ─────────────────────────────────────────────────────────────────────────────
 * Getting into a car does not START the radio; it OPENS A WINDOW onto something
 * that was already playing. Getting out closes the window and **the track keeps
 * advancing**. That is why {@link createRadio}'s `update` runs the clock whether
 * or not anybody is listening, and it is the whole reason the owner's headline
 * case needs no special case at all:
 *
 *   out at 0:30 → ten seconds on foot → back in at 0:40.
 *
 * A model that paused on `exit()` would need a "how long were they away" clock
 * somewhere else, and a model that re-picked on `enter()` would hand the player
 * a different song every time they got out to shoot somebody — which is exactly
 * the immersion break this feature exists to avoid.
 *
 * There is ONE case where tracking stops, and it falls out of the same clock:
 * a track that **finished while they were away** is over, so there is nothing
 * to resume and the next entry picks fresh. A track that finishes while they
 * ARE in the car rolls straight into the next one.
 *
 * ⚠ The fiction is not quite total, on purpose: nothing is tracked until the
 * FIRST entry, because the owner's rule is that the first track plays FROM 0.
 * Picking at construction would resume the player into the middle of a song
 * they had never heard the start of, at level load, for their first ever drive.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ RULE 4 — ONE PRIVATE STREAM, AND IT IS NOT A SIM STREAM
 * ─────────────────────────────────────────────────────────────────────────────
 * `Math.random()` is banned under `src/`, so the track pick draws from ONE
 * private `mulberry32` this module owns, written out as an explicit
 * `mulberry32(hashSeed(seed) + hashSeed(SALT))` rather than through `streamRng`
 * — the `audio/mixer.js` idiom, and for the same reason: `streamRng` is the
 * per-GENERATION-STAGE discipline and a radio is emphatically not a generation
 * stage. Nothing in the simulation reads this stream and nothing in the
 * simulation can advance it; which song is on depends on how the player played,
 * and the only consequence is which song is on.
 *
 * ⚠ **DICE ARE DRAWN ONLY WHEN A TRACK IS ACTUALLY PICKED.** Never per frame,
 * never on `exit()`, never on an `enter()` that resumes, and never on an
 * `enter()`/`update()` that finds nothing to do. A die rolled on a quiet frame
 * makes the sequence of songs depend on how long the player drove, which is the
 * dependency rule 4 exists to keep out.
 *
 * ⚠ **NO-IMMEDIATE-REPEAT IS ARITHMETIC, NOT A RE-ROLL.** A pick draws in
 * `[0, n-1)` and SHIFTS past the previous index, so it is uniform over the
 * `n - 1` songs that are not the one that just played and it costs EXACTLY ONE
 * die every time. Roll-and-retry spends a variable number of dice — still
 * deterministic, so a determinism test cannot tell the two apart, which is why
 * `tests/radio.test.mjs` (f) mirrors this arithmetic explicitly. This is
 * `docs/dialogue.md`'s rule for the line banks, and the reasoning is identical.
 * A one-track station degrades to repeating that track and draws NOTHING.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ SOLE PUBLISHER OF `'radio:track'`, AND IT FIRES ON TRANSITIONS ONLY
 * ─────────────────────────────────────────────────────────────────────────────
 * The `Wallet.js` / `'player:cash'` idiom, for the `'hud:prompt'` reason: two
 * systems on one channel means the HUD shows whichever spoke last. It fires when
 * a new track STARTS and when `enter()` RESUMES one — never per frame. Both are
 * moments the HUD has to be able to name what is playing, because **the station
 * is said out loud every time a track starts**, which is the owner's
 * requirement and the reason `src/game/radio.js` groups tracks by station at all.
 * `exit()` publishes nothing: leaving the car did not change what the station is
 * playing.
 *
 * ⚠ The payload carries `file` as the **SERVED URL** — `base` already joined to
 * the track's relative path. The join happens here, exactly once; the wiring
 * must not re-derive it, and there is therefore no second place that knows how
 * `RADIO_BASE` relates to a file name.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ IT IS TOLD A REAL SPAN, NOT THE SIM STEP
 * ─────────────────────────────────────────────────────────────────────────────
 * `update(dt)` must be driven from a REAL clock — `GameAudio` calls it from
 * `render`, with the same real millisecond span it already computes for
 * `sfx.update`, for the reason recorded in that file's two-hook header: five
 * overlays can freeze the fixed step, and a station that stops advancing behind
 * a pause menu is a station that lies about where the song is when the menu
 * closes. **Do not compensate for that here** — this module owns no clock and
 * has no opinion about time scale.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NOT IN THIS ROUND
 * ─────────────────────────────────────────────────────────────────────────────
 * No dial UI (`setStation` exists and nothing calls it), no per-station volume,
 * no DJ, no ads, no news bulletins, no shuffle bag, no "recently played" beyond
 * the one index the no-repeat rule needs, and no subscriptions — this module
 * publishes and is CALLED; it listens to nothing.
 *
 * @module engine/systems/Radio
 */

import { hashSeed, mulberry32 } from '../math/rng.js';

/**
 * THE TUNING. One value, because the interesting numbers are content:
 * `RADIO_TUNE` in `src/game/radio.js` holds the gain and the fade, and the
 * durations are MEASURED into `radioAssets.js` by `npm run bgm`.
 */
export const RADIO = Object.freeze({
  /**
   * Seconds. The floor a track's `dur` is held to when the injected number is
   * missing, zero or nonsense. ⚠ It is a GUARD against a malformed injection,
   * never a default anybody should rely on: every real row carries a measured
   * duration, and a station whose songs are all one second long is a bug you
   * want to hear. It exists because a `dur` of 0 in a `while` loop would be an
   * infinite loop — and the reason it cannot be even with this floor is that
   * the rollover below is an `if`, never a `while`.
   */
  minDur: 1,
});

/** ⚠ NOT a sim stream — see the header. One salt, in the mixer's shape. */
const SALT = 'audio:radio';

/**
 * @param {any} n
 * @returns {boolean} A real, usable number. The `Wallet.js#usable` guard: a
 *   `NaN` out of a malformed injection must not poison the on-air clock.
 */
const usable = (n) => typeof n === 'number' && Number.isFinite(n);

/**
 * Joins the table's `base` to a track's relative path, ONCE, here.
 *
 * ⚠ This is the only place in the project that knows a served radio url is
 * `base + '/' + file`. The wiring is handed the finished string on
 * `'radio:track'` precisely so a second copy of this rule cannot exist —
 * `links.js` mirroring `countryroads.js`'s joint constants is what that costs.
 *
 * @param {any} base
 * @param {any} file
 * @returns {string} A served path, or `''` when there is no file to serve.
 */
const joinUrl = (base, file) => {
  const f = typeof file === 'string' ? file.replace(/^\/+/, '') : '';
  if (!f) return '';
  const b = typeof base === 'string' ? base.replace(/\/+$/, '') : '';
  return b ? `${b}/${f}` : `/${f}`;
};

/**
 * @param {any} t A track row.
 * @returns {number} Its duration in seconds, floored at {@link RADIO}`.minDur`.
 */
const durOf = (t) => (usable(t?.dur) && t.dur > 0 ? t.dur : RADIO.minDur);

/**
 * @typedef {object} OnAir What is playing, right now, on the station the dial is
 *   on. The payload of `'radio:track'` is exactly this object, freshly built.
 * @property {string|null} stationId
 * @property {string} stationName ⚠ Said every time a track starts. The whole
 *   reason the content is grouped by station.
 * @property {string|null} trackId
 * @property {string} title
 * @property {string} file The SERVED URL — `base` already joined. Do not re-derive.
 * @property {number} offset Seconds into the track. This is the number the
 *   window-onto-the-station idea is made of.
 * @property {number} dur Seconds. Measured by `npm run bgm`.
 */

/**
 * The car radio: one dial, one track, and a clock that never stops.
 *
 * @param {Object} [o]
 * @param {any} [o.table] `createRadioTable()` from `src/game/radio.js`, injected
 *   by `PlayRoute.jsx` exactly like the sound, weapon and faction tables. ⚠ The
 *   engine never imports `src/game/`, so with no table this whole module holds
 *   no stations, picks nothing, draws no dice and publishes nothing — which is
 *   what keeps hero mode's golden checksum **1878938342** safe BY CONSTRUCTION.
 *   Stations with no tracks are dropped here as well as in the content builder;
 *   an "on air with nothing to play" state is one this model never has to hold.
 * @param {any} [o.bus] `'radio:track'` on transitions, and nothing else, ever.
 *   This module is its SOLE publisher and SUBSCRIBES TO NOTHING — `GameAudio`
 *   owns the join to `'player:vehicle'`, because deciding what "the player is in
 *   a car" means is not a question about music.
 * @param {number|string} [o.seed] The level seed. One private stream, salted.
 * @returns {Object}
 */
export function createRadio(o = {}) {
  const { table = null, bus = null, seed = 0 } = o ?? {};

  /** The url prefix from the table. `''` when there is no table. */
  const base = typeof table?.base === 'string' ? table.base : '';

  /**
   * The dial's contents. Filtered on the way in: a station with no tracks is
   * not a station, and dropping it here means no branch below has to ask.
   * @type {any[]}
   */
  const stations = (Array.isArray(table?.stations) ? table.stations : []).filter(
    (s) => s && typeof s === 'object' && Array.isArray(s.tracks) && s.tracks.length > 0,
  );

  /**
   * ⚠ THE ONE PRIVATE STREAM. Written as an explicit `mulberry32(hashSeed(seed)
   * + hashSeed(SALT))` rather than through `streamRng` ON PURPOSE — the
   * `audio/mixer.js` idiom. See the header: nothing in the sim reads it and
   * nothing in the sim can advance it.
   */
  const rng = mulberry32((hashSeed(seed) + hashSeed(SALT)) >>> 0);

  // ── the state ───────────────────────────────────────────────────────────────
  /** Which station the dial is on. `-1` only when there are none. */
  let stationIdx = stations.length ? 0 : -1;
  /**
   * Which track of that station is on air, or `-1` for "nothing is being
   * tracked" — before the first entry, and after a track ran out unheard.
   */
  let trackIdx = -1;
  /**
   * The track before this one, kept ONLY so the next pick can exclude it. It
   * survives `trackIdx` going to -1, which is what stops the song that just
   * ended unheard being the one handed back on the next entry.
   */
  let prevIdx = -1;
  /** Seconds into the current track. Advances whether or not anyone is hearing it. */
  let offset = 0;
  /** Is the window open? Nothing about the CLOCK depends on this — that is the point. */
  let listening = false;
  let disposed = false;

  // ── the counters (for `stats()`, `DebugPanel` and the suites) ──────────────
  /**
   * `'radio:track'` announcements: every pick, plus every resume. This is "how
   * many times has the player been told what is on", which is not the same
   * question as the next one.
   */
  let started = 0;
  /**
   * Tracks CHOSEN. On a station with more than one track this is exactly the
   * number of dice drawn, which is what makes rule 4 observable from outside.
   */
  let picks = 0;

  // ── the table, read through guards ─────────────────────────────────────────

  /** @returns {any|null} The station the dial is on. */
  const _station = () => (stationIdx >= 0 ? (stations[stationIdx] ?? null) : null);

  /** @returns {any|null} The track on air, or null when nothing is tracked. */
  const _track = () => {
    const st = _station();
    if (!st || trackIdx < 0) return null;
    return st.tracks[trackIdx] ?? null;
  };

  /**
   * ⚠ A FRESH OBJECT EVERY TIME. The inverse of the director's reused `hud:blips`
   * scratch array: this crosses the bus and a HUD keeps it for as long as the
   * readout is up, so a shared object would rewrite a banner that is still on
   * screen with the next song's name.
   *
   * @returns {OnAir|null}
   */
  const _snapshot = () => {
    const st = _station();
    const tr = _track();
    if (!st || !tr) return null;
    return {
      stationId: st.id ?? null,
      stationName: st.name ?? st.id ?? '',
      trackId: tr.id ?? null,
      title: tr.title ?? '',
      file: joinUrl(base, tr.file),
      offset,
      dur: durOf(tr),
    };
  };

  // ── the channel ────────────────────────────────────────────────────────────

  /**
   * ⚠ TRANSITIONS ONLY, and called from exactly two places (a pick, and a
   * resume) so "never per frame" is a property of the code rather than of
   * everybody remembering.
   */
  const _announce = () => {
    const snap = _snapshot();
    if (!snap) return;
    started++;
    bus?.emit?.('radio:track', snap);
  };

  // ── the pick ───────────────────────────────────────────────────────────────

  /**
   * THE ONE DIE. Uniform over the `n - 1` tracks that are NOT {@link prevIdx}.
   *
   * ⚠ The shift is the whole point: `floor(rng() * (n - 1))` names one of the
   * others directly, and stepping past `prevIdx` maps it back into `[0, n)`.
   * Roll-and-retry would spend a variable number of dice and make the banks
   * non-composable — see the header, and `docs/dialogue.md` for the same rule
   * on the line banks. ⚠ `n === 1` draws NOTHING and returns the only track
   * there is: a station of one song repeats, which is the honest degradation.
   *
   * @param {number} n Track count, >= 1.
   * @returns {number} An index in `[0, n)`.
   */
  const _pickIndex = (n) => {
    if (n <= 1) return 0;
    const p = prevIdx;
    if (p < 0 || p >= n) return Math.min(n - 1, Math.floor(rng() * n));
    const k = Math.min(n - 2, Math.floor(rng() * (n - 1)));
    return k >= p ? k + 1 : k;
  };

  /**
   * Picks a track, puts it on air at zero and says so. The ONLY place a die is
   * drawn and the only place `picks` moves.
   *
   * @returns {boolean} Something is now playing.
   */
  const _start = () => {
    const st = _station();
    if (!st) return false;
    // ⚠ Guarded: after a track ends unheard `trackIdx` is -1 and `prevIdx`
    // already holds the song that ran out. Clobbering it here would let the
    // next entry hand back the one the player just missed the end of.
    if (trackIdx >= 0) prevIdx = trackIdx;
    trackIdx = _pickIndex(st.tracks.length);
    offset = 0;
    picks++;
    _announce();
    return true;
  };

  const api = {
    name: 'radio',

    // ── the clock ────────────────────────────────────────────────────────────

    /**
     * Advances the on-air clock. ⚠ **CALL IT UNCONDITIONALLY AND WITH A REAL
     * SPAN** — see the header. The station does not care whether anybody is in
     * a car, and it does not care whether an overlay has frozen the sim.
     *
     * Two ways a track can run out, and they are different events:
     *   • **listening** — roll straight into a new one. The player hears the
     *     next song start, which is what a radio does.
     *   • **not listening** — stop tracking. The song is over and there is
     *     nothing left to resume, so the next entry picks fresh. This is the
     *     ONE case where the window-onto-the-station fiction ends.
     *
     * ⚠ **`if`, NEVER `while`.** A frame hitch (or a tab left in the background)
     * hands this a dt worth ten songs, and a `while` would spend ten dice and
     * skip nine tracks the player never heard — `EnemyManager._stepFire` and
     * `Weapons.js`'s step 3 both paid for this exact loop once.
     *
     * @param {number} dt Seconds of REAL time.
     */
    update(dt) {
      if (disposed || !usable(dt) || dt <= 0) return;
      // Nothing is being tracked: there is no clock to run, and — rule 4 — no
      // die to draw. A station only starts a song when somebody opens a window.
      if (trackIdx < 0) return;
      offset += dt;
      if (offset < durOf(_track())) return;
      if (listening) {
        _start();
      } else {
        prevIdx = trackIdx;
        trackIdx = -1;
        offset = 0;
      }
    },

    // ── the window ───────────────────────────────────────────────────────────

    /**
     * The player got into a car. Opens the window onto whatever the station is
     * doing — it does NOT start the radio.
     *
     * Three cases and no dice in two of them: nothing tracked (the first entry,
     * or the entry after a track ended unheard) picks and plays from 0; a track
     * still running is RESUMED at its live offset and re-announced, because the
     * HUD has to be able to name what is on; and calling it twice is idempotent,
     * which matters because a re-announce would restart the fade under an
     * already-playing song.
     *
     * @returns {boolean} Something is playing now.
     */
    enter() {
      if (disposed) return false;
      if (!_station()) return false;
      const was = listening;
      listening = true;
      if (trackIdx < 0) return _start();
      if (!was) _announce();
      return true;
    },

    /**
     * The player got out. Closes the window and NOTHING ELSE — no dice, no
     * event, and above all no touching {@link offset}. The track keeps
     * advancing; that is the feature.
     */
    exit() {
      if (disposed) return;
      listening = false;
    },

    // ── the dial ─────────────────────────────────────────────────────────────

    /**
     * Tunes to a station by id and picks a fresh track on it.
     *
     * ⚠ **NOTHING CALLS THIS YET.** It is the seam a dial UI would use, and it
     * ships now because the alternative is a model that would have to grow a
     * concept of "more than one station" later, when the second station is
     * added and the change is no longer free. `src/game/radio.js` already
     * iterates a list.
     *
     * ⚠ `prevIdx` is reset only on a REAL station change: an index means a
     * different song on a different station, so carrying it across would
     * exclude an arbitrary track for no reason. Re-tuning to the station you
     * are already on keeps it, so it cannot hand you the song already playing.
     *
     * @param {string} id
     * @returns {boolean} The station exists and is now on air. `listening` is
     *   untouched — the dial and the car door are different questions.
     */
    setStation(id) {
      if (disposed) return false;
      const i = stations.findIndex((s) => s.id === id);
      if (i < 0) return false;
      if (i !== stationIdx) {
        stationIdx = i;
        trackIdx = -1;
        prevIdx = -1;
      }
      offset = 0;
      return _start();
    },

    // ── readouts ─────────────────────────────────────────────────────────────

    /**
     * @returns {OnAir|null} What the station is playing and how far in, or null
     *   when nothing is tracked (before the first entry; after a track ended
     *   unheard) or when no table was injected. ⚠ It reads the same whether or
     *   not anybody is listening — ask {@link listening} for that.
     */
    get onAir() {
      return _snapshot();
    },

    /** @returns {boolean} Is the player in a car with the window open? */
    get listening() {
      return listening;
    },

    /** @returns {any|null} The `StationDef` the dial is on, verbatim from the table. */
    get station() {
      return _station();
    },

    /**
     * @returns {{station: string|null, track: string|null, offset: number,
     *   dur: number, listening: boolean, started: number, picks: number}} A
     *   plain object for `Gameplay.stats()`, the debug panel and the suites.
     *   ⚠ `started` counts ANNOUNCEMENTS (picks plus resumes); `picks` counts
     *   tracks CHOSEN, which on a multi-track station is the die count.
     */
    stats() {
      const st = _station();
      const tr = _track();
      return {
        station: st?.id ?? null,
        track: tr?.id ?? null,
        offset,
        dur: tr ? durOf(tr) : 0,
        listening,
        started,
        picks,
      };
    },

    /**
     * Idempotent. There is nothing to unsubscribe — this module listens to
     * nothing — so it simply freezes: `update` becomes a no-op, `enter()` and
     * `setStation()` refuse, and nothing more is published, so a system
     * disposed mid-frame cannot repaint a HUD on its way out. The state is left
     * standing rather than cleared, the `Drone#dispose` precedent: a getter
     * read during teardown returns the last true thing it knew.
     */
    dispose() {
      if (disposed) return;
      disposed = true;
    },
  };

  return api;
}
